import * as React from 'react';

/** Densidade visual dos itens do rail primário, da mais folgada para a mais compacta. */
export type RailDensity = 'full' | 'compact' | 'icons';

// 81 (header 48 + separador 17 + margem 16) + rodapé de três elementos: os dois fixos (116) + o
// maior condicional (boleto, 48). Incidente e boleto juntos (~289) estouram e o rail volta a rolar.
export const RAIL_CHROME_HEIGHT = 245;

// Espelha as classes de `AppSidebar` e `SidebarMenu` — manter em sincronia.
const DENSITY_METRICS: Record<RailDensity, { row: number; gap: number }> = {
  full: { row: 48, gap: 16 },
  compact: { row: 44, gap: 8 },
  icons: { row: 32, gap: 8 },
};

/** Abaixo disso os `itemCount` itens não cabem sem scroll nessa densidade. */
export function railMinViewportHeight(density: RailDensity, itemCount: number): number {
  if (itemCount <= 0) return RAIL_CHROME_HEIGHT;
  const { row, gap } = DENSITY_METRICS[density];
  return RAIL_CHROME_HEIGHT + itemCount * (row + gap) - gap;
}

/** Se o item do rail mostra o rótulo abaixo do ícone — em `icons` quem carrega o título é o Tooltip. */
export const railShowsTitle = (density: RailDensity, showTitle = true): boolean => showTitle && density !== 'icons';

const noopUnsubscribe = () => {
  // Sem window (SSR/teste) não há o que desinscrever.
};

function useMediaQueryMatches(query: string): boolean {
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined') return noopUnsubscribe;
      const media = window.matchMedia(query);
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    },
    [query]
  );
  const getSnapshot = React.useCallback(() => typeof window !== 'undefined' && window.matchMedia(query).matches, [query]);

  return React.useSyncExternalStore(subscribe, getSnapshot, () => true);
}

// Media query estático não serve: sem a contagem de itens, frota de 6 compactaria onde ainda cabe.
export function useRailDensity(itemCount: number): RailDensity {
  const fitsFull = useMediaQueryMatches(`(min-height: ${railMinViewportHeight('full', itemCount)}px)`);
  const fitsCompact = useMediaQueryMatches(`(min-height: ${railMinViewportHeight('compact', itemCount)}px)`);

  if (fitsFull) return 'full';
  if (fitsCompact) return 'compact';
  return 'icons';
}
